import {
  FormField,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { Queue } from 'lib-shared-types-api';
import { useCallback, useEffect, useRef, useState } from 'react';

import { appActions } from 'app/app.actions';
import { InputQueuesSelect } from 'components/inputQueuesSelect';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './conversationQueueTransferModal.module.scss';

/** A component for transferring a conversation to a different queue. */
export const ConversationQueueTransferModal = createAppModal(
  { type: 'app:conversationQueueTransfer' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const [selectedQueues, setSelectedQueues] = useState<Queue[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const queue: Queue | undefined = selectedQueues[0];
    const csid = modal.arg.csid;

    /** Handles closing the transfer to queue modal. */
    const handleClose = useCallback(() => {
      ampli.modalTransferToQueueCancelClicked();
      hide?.();
    }, [hide]);

    // Click the dropdown button to open it when modal opens
    useEffect(() => {
      if (isOpen) {
        // Small delay to ensure the modal is fully rendered
        const timer = setTimeout(() => {
          // The InputSelectionList uses a button to open the dropdown
          const button = containerRef.current?.querySelector('button');
          button?.click();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [isOpen]);

    /** Handles confirming the transfer to queue. */
    const handleConfirm = useCallback(async () => {
      if (!csid || !queue) {
        return;
      }

      ampli.modalTransferToQueueConfirmClicked({
        csid,
        queueId: queue.id,
      });

      dispatch(
        appActions.conversationTransferRequest.pending({
          request: {
            type: 'internal_queue',
            csid,
            queue,
          },
        }),
      );

      handleClose();
    }, [csid, dispatch, handleClose, queue]);

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={`Transfer ${csid} to a different queue`}
          onClose={handleClose}
        />

        <ModalBody>
          <div ref={containerRef}>
            <FormField>
              <InputQueuesSelect
                value={selectedQueues}
                onChange={({ selectedValues }) =>
                  setSelectedQueues(selectedValues)
                }
              />
            </FormField>
          </div>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleClose}
          confirmName="Transfer"
          confirmProps={{
            id: 'transfer-queue-confirm-button',
            disabled: !queue,
          }}
          confirmOnClick={handleConfirm}
        />
      </Modal>
    );
  },
);
